import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, Tag, ArrowRight, ChevronRight, CheckCircle, Clock } from 'lucide-react';
import PageHero from '../components/PageHero';
import { fadeUp, stagger, viewportOpts } from '../hooks/useScrollAnimation';
import { blogPosts, parentingTips } from '../data/blogPosts';

const categories = ["All", "Parenting Tips", "School Updates", "Activities", "Child Development", "Events"];

export default function Blog() {
  const [activeCategory, setActiveCategory] = useState("All");
  const featured = blogPosts.find(p => p.featured);
  const filtered = activeCategory === "All" ? blogPosts.filter(p => !p.featured) : blogPosts.filter(p => p.category === activeCategory && !p.featured);

  return (
    <div>
      <PageHero title="Our Blog" subtitle="Tips, stories, updates, and expert insights from the Happy Kidz community." breadcrumb="Blog" />

      {/* Featured Post */}
      {activeCategory === "All" && featured && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial="hidden" whileInView="visible" viewport={viewportOpts} variants={fadeUp}>
              <span className="section-label mb-5 inline-block">Featured Article</span>
              <div className="grid lg:grid-cols-5 gap-0 bg-white rounded-3xl overflow-hidden shadow-xl border border-gray-100">
                <div className="lg:col-span-2 h-64 lg:h-auto overflow-hidden">
                  <img src={featured.image} alt={featured.title} className="w-full h-full object-cover" />
                </div>
                <div className="lg:col-span-3 p-8 lg:p-12 flex flex-col justify-center">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#C8161D] bg-[#FDE8E9] px-3 py-1 rounded-full mb-4 w-fit">
                    <Tag size={11} /> {featured.category}
                  </span>
                  <h2 className="font-nunito font-extrabold text-3xl text-[#1A1A1A] mb-3 leading-tight">{featured.title}</h2>
                  <p className="text-gray-600 leading-relaxed mb-4">{featured.excerpt}</p>
                  {/* Parenting Tips List */}
                  <div className="grid grid-cols-2 gap-2 mb-6">
                    {featured.tips?.map((t, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle size={14} className="text-[#9DC41E] flex-shrink-0" />
                        <span className="text-gray-600 text-sm">{t}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-4 text-gray-400 text-sm mb-5">
                    <span className="flex items-center gap-1.5"><User size={13} /> {featured.author}</span>
                    <span className="flex items-center gap-1.5"><Calendar size={13} /> {featured.date}</span>
                  </div>
                  <Link to="/blog" className="btn-primary w-fit" data-testid="featured-post-btn">Read Full Article <ArrowRight size={16} /></Link>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Parenting Tips Quick Card */}
      {activeCategory === "All" && (
        <section className="py-12 bg-[#F3F9E2]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div className="text-center mb-8" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={fadeUp}>
              <span className="section-label">Quick Tips</span>
              <h2 className="font-nunito font-extrabold text-3xl text-[#1A1A1A] mt-2">7 Parenting Tips from Our Educators</h2>
            </motion.div>
            <motion.div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={stagger}>
              {parentingTips.slice(0, 4).map((t, i) => (
                <motion.div key={i} variants={fadeUp} className="bg-white rounded-2xl p-5 shadow-sm border border-[#9DC41E]/20 hover-card">
                  <div className="w-8 h-8 bg-[#9DC41E] rounded-full flex items-center justify-center text-white font-nunito font-bold text-sm mb-3">{i + 1}</div>
                  <h4 className="font-nunito font-bold text-[#1A1A1A] mb-1 text-sm">{t.tip}</h4>
                  <p className="text-gray-500 text-xs leading-relaxed">{t.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* Category Filter */}
      <section className="py-5 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3" data-testid="blog-categories">
            {categories.map(cat => (
              <button key={cat} onClick={() => setActiveCategory(cat)}
                data-testid={`category-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${activeCategory === cat ? 'bg-[#C8161D] text-white shadow-md' : 'bg-white text-gray-600 hover:bg-[#FDE8E9] hover:text-[#C8161D] border border-gray-200'}`}>
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-14 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={stagger}>
            {filtered.map((post, i) => (
              <motion.article key={i} variants={fadeUp} className="hover-card bg-white rounded-3xl overflow-hidden border border-gray-100" data-testid={`blog-post-${i}`}>
                <div className="h-52 overflow-hidden">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#C8161D] bg-[#FDE8E9] px-3 py-1 rounded-full mb-3"><Tag size={11} /> {post.category}</span>
                  <h3 className="font-nunito font-bold text-xl text-[#1A1A1A] mb-2 leading-snug line-clamp-2">{post.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-2">{post.excerpt}</p>
                  <div className="flex items-center justify-between text-gray-400 text-xs border-t border-gray-100 pt-4">
                    <span className="flex items-center gap-1.5"><User size={12} /> {post.author}</span>
                    <span className="flex items-center gap-1.5"><Calendar size={12} /> {post.date}</span>
                  </div>
                  <Link to="/blog" className="mt-4 text-[#C8161D] font-semibold text-sm inline-flex items-center gap-1 hover:gap-2 transition-all">Read More <ChevronRight size={14} /></Link>
                </div>
              </motion.article>
            ))}
          </motion.div>
          {filtered.length === 0 && (
            <div className="text-center py-16 text-gray-400"><p className="text-lg">No posts found in this category.</p></div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-gradient-to-r from-[#C8161D] to-[#9B1015] text-white">
        <motion.div className="max-w-4xl mx-auto px-4 text-center" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={fadeUp}>
          <h2 className="font-nunito font-extrabold text-3xl mb-3">Stay Connected with Happy Kidz</h2>
          <p className="text-red-100 mb-6">Follow us for the latest updates, tips, and school news.</p>
          <Link to="/contact" className="bg-white text-[#C8161D] font-nunito font-bold rounded-full px-8 py-4 hover:bg-red-50 transition-all hover:-translate-y-1 inline-flex items-center gap-2 shadow-lg" data-testid="blog-contact-btn">Contact Us <ArrowRight size={18} /></Link>
        </motion.div>
      </section>
    </div>
  );
}
